import React, { useState, useEffect, useCallback } from 'react';
import { toast } from '../../../components/shared/toast';
import { apiClient } from '../../../api/apiClient';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import LoadingOverlay from '../../../components/shared/LoadingOverlay';
import EmptyState from '../../../components/shared/EmptyState';
import { getBackendOrigin, getSecureAssetUrl } from '../../../utils/backendUrl';

function ArtistsOwnershipTab({ song }) {
  const [ownershipData, setOwnershipData] = useState({
    summary: { percentage: 0, is_recordlabel: false, is_lyrics: false, is_musician: false, is_singer: false },
    recordLabels: [],
    lyrics: [],
    musicians: [],
    singers: []
  });
  const [isLoading, setIsLoading] = useState(true);

  // Remove confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Download confirmation modal state
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [docToDownload, setDocToDownload] = useState(null);

  const fetchOwnership = useCallback(async () => {
    if (!song || !song.id) return;
    try {
      setIsLoading(true);
      const res = await apiClient(`/songs/${song.id}/ownership`);
      if (res) {
        setOwnershipData(res);
      }
    } catch (err) {
      console.error('Error fetching song ownership data:', err);
      toast.error('Failed to load song ownership details.');
    } finally {
      setIsLoading(false);
    }
  }, [song]);

  useEffect(() => {
    fetchOwnership();
  }, [song?.id]);

  const handleDownloadClick = (doc, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setDocToDownload(doc);
    setShowDownloadModal(true);
  };

  const handleConfirmDownload = async () => {
    if (!docToDownload) return;
    const fileUrl = getSecureAssetUrl(docToDownload.document_url || `${getBackendOrigin()}/uploads/documents/sample-document.pdf`);
    setShowDownloadModal(false);
    try {
      toast.info('Downloading document...');
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = blobUrl;
      const fileName = `${(docToDownload.rawName || 'document').replace(/[^a-z0-9]/gi, '_')}.pdf`;
      link.setAttribute('download', fileName);
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Document downloaded successfully!');
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download document file.');
    } finally {
      setDocToDownload(null);
    }
  };

  const handleCardClick = (doc, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    window.history.pushState({ documentId: doc.ownershipId }, '', '/ownership');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleRemoveClick = (mappingId, categoryKey, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const catMap = {
      label: 'recordlabel',
      recordlabel: 'recordlabel',
      lyrics: 'lyrics',
      melody: 'musician',
      musician: 'musician',
      singer: 'singer'
    };
    const category = catMap[categoryKey] || categoryKey;
    setMappingToDelete({ mappingId, category });
    setShowConfirmModal(true);
  };

  const handleConfirmRemove = async () => {
    if (!mappingToDelete || !song || !song.id) return;
    try {
      setIsDeleting(true);
      const mId = typeof mappingToDelete === 'object' ? mappingToDelete.mappingId : mappingToDelete;
      const cat = typeof mappingToDelete === 'object' ? mappingToDelete.category : '';
      const url = `/songs/${song.id}/ownership/${mId}${cat ? `?category=${cat}` : ''}`;
      const res = await apiClient(url, {
        method: 'DELETE'
      });
      if (res && res.success !== false) {
        toast.success('Ownership document mapping removed successfully');
        setShowConfirmModal(false);
        setMappingToDelete(null);
        fetchOwnership();
      }
    } catch (err) {
      console.error('Error removing ownership mapping:', err);
      toast.error(err.message || 'Failed to remove ownership document mapping.');
    } finally {
      setIsDeleting(false);
    }
  };

  const summary = ownershipData.summary || { percentage: 0, is_recordlabel: false, is_lyrics: false, is_musician: false, is_singer: false };

  // Determine primary artist subtitles for each section
  const labelArtistName = ownershipData.recordLabels?.[0]?.artist?.name || 'Recode label';
  const lyricsArtistName = ownershipData.lyrics?.[0]?.artist?.name || 'Lyrics artist';
  const musicianArtistName = ownershipData.musicians?.[0]?.artist?.name || 'Melody artist';
  const singerArtistName = ownershipData.singers?.[0]?.artist?.name || 'Singer artist';

  const columns = [
    {
      id: 'label',
      title: 'Recode label',
      subtitle: labelArtistName,
      colorClass: 'purple-theme',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 22h18" />
          <path d="M6 18V9h12v9" />
          <path d="m12 2-10 7h20Z" />
          <path d="M10 18v-4h4v4" />
        </svg>
      ),
      files: ownershipData.recordLabels || []
    },
    {
      id: 'lyrics',
      title: 'Lyrics',
      subtitle: lyricsArtistName,
      colorClass: 'coral-theme',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
        </svg>
      ),
      files: ownershipData.lyrics || []
    },
    {
      id: 'melody',
      title: 'Melody',
      subtitle: musicianArtistName,
      colorClass: 'green-theme',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 18V5l12-2v13" />
          <circle cx="6" cy="18" r="3" />
          <circle cx="18" cy="16" r="3" />
        </svg>
      ),
      files: ownershipData.musicians || []
    },
    {
      id: 'singer',
      title: 'Singer',
      subtitle: singerArtistName,
      colorClass: 'blue-theme',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="22" />
        </svg>
      ),
      files: ownershipData.singers || []
    }
  ];

  return (
    <div className="artists-ownership-container">
      {isLoading && <LoadingOverlay isLoading={true} message="Loading ownership details..." />}

      {/* Top Split Header */}
      <div className="ownership-header">
        <div className="ownership-header-left">
          <h2 className="ownership-title">{summary.percentage}% owners by Us</h2>
          <p className="ownership-subtitle">Total ownership</p>
        </div>

        <div className="ownership-header-center">
          <div className="split-labels-row">
            <span style={{ width: '50%', textAlign: 'center' }}>50%</span>
            <span style={{ width: '25%', textAlign: 'center' }}>25%</span>
            <span style={{ width: '25%', textAlign: 'center' }}>25%</span>
          </div>
          <div className="split-bar-track">
            <div 
              className={`split-bar-segment producer ${summary.is_recordlabel ? 'active' : 'inactive'}`} 
              style={{ width: '50%', background: summary.is_recordlabel ? '#9333ea' : '#cbd5e1' }}
            ></div>
            <div 
              className={`split-bar-segment lyrics ${summary.is_lyrics ? 'active' : 'inactive'}`} 
              style={{ width: '25%', background: summary.is_lyrics ? '#f97316' : '#cbd5e1' }}
            ></div>
            <div 
              className={`split-bar-segment melody ${summary.is_musician ? 'active' : 'inactive'}`} 
              style={{ width: '25%', background: summary.is_musician ? '#10b981' : '#cbd5e1' }}
            ></div>
          </div>
          <div className="split-labels-row split-names">
            <span style={{ width: '50%', textAlign: 'center' }}>Producer</span>
            <span style={{ width: '25%', textAlign: 'center' }}>Lyrics</span>
            <span style={{ width: '25%', textAlign: 'center' }}>Melody</span>
          </div>
        </div>

        <div className="ownership-header-right">
          <span className="additional-title">Additional</span>
          <div 
            className="additional-bar" 
            style={{ background: summary.is_singer ? '#3b82f6' : '#cbd5e1' }}
          ></div>
          <span className="additional-subtitle">Singer</span>
        </div>
      </div>

      <hr className="section-divider" />

      {/* Grid columns */}
      <div className="ownership-columns-grid">
        {columns.map((col) => (
          <div key={col.id} className="ownership-column">
            {/* Column Header */}
            <div className="ownership-col-header">
              <div className={`ownership-col-icon-wrapper ${col.colorClass}`}>
                {col.icon}
              </div>
              <div className="ownership-col-info">
                <h4 className="ownership-col-title">{col.title}</h4>
                <p className="ownership-col-subtitle">{col.subtitle}</p>
              </div>
            </div>

            {/* Column Files */}
            <div className="ownership-col-files">
              {col.files.length > 0 ? (
                col.files.map((file, idx) => (
                  <div 
                    key={file.mappingId || idx} 
                    className={`file-card ${col.colorClass}`}
                  >
                    <div className="file-card-top">
                      <div 
                        className="file-icon-title" 
                        onClick={(e) => handleCardClick(file, e)}
                        style={{ cursor: 'pointer' }}
                        title="Click to view ownership document"
                      >
                        <svg className="file-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                        <span className="file-name" title={file.name}>{file.name}</span>
                      </div>
                      <button 
                        type="button"
                        className="file-menu-btn"
                        onClick={(e) => handleDownloadClick(file, e)}
                        title="Download Document PDF"
                        style={{ cursor: 'pointer', zIndex: 10, position: 'relative' }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                      </button>
                    </div>
                    
                    <div className="file-card-details">
                      <span className="file-size-type">{file.size || 'PDF Document'}</span>
                    </div>

                    <div className="file-card-badge-row">
                      <span className="file-badge">{file.badge || 'Agreement'}</span>
                    </div>

                    <div className="file-card-checkbox-row">
                      <label className="file-checkbox-label">
                        <input 
                          type="checkbox" 
                          checked={file.is_ownership} 
                          readOnly 
                          className="file-checkbox" 
                        />
                        <span className="checkbox-custom-style"></span>
                        <span className="checkbox-text">Add to ownership</span>
                      </label>
                    </div>

                    <div className="file-card-actions">
                      <button 
                        type="button"
                        className="file-remove-btn" 
                        onClick={(e) => handleRemoveClick(file.mappingId, col.id, e)}
                        style={{ cursor: 'pointer', zIndex: 10, position: 'relative' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                        <span>Remove</span>
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <EmptyState 
                  title="No Record Found" 
                  subtitle={`No documents linked to ${col.title}`} 
                  hasSearch={false} 
                />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Download Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDownloadModal}
        type="info"
        title="Download Document"
        message={`Are you sure you want to download "${docToDownload?.name || 'document'}"?`}
        confirmLabel="Download"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDownload}
        onCancel={() => {
          setShowDownloadModal(false);
          setDocToDownload(null);
        }}
      />

      {/* Remove Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        type="danger"
        title="Remove Ownership Mapping"
        message="Are you sure you want to remove this ownership document mapping from this song? This will update the song's ownership flags."
        confirmLabel={isDeleting ? 'Removing...' : 'Remove'}
        cancelLabel="Cancel"
        onConfirm={handleConfirmRemove}
        onCancel={() => {
          setShowConfirmModal(false);
          setMappingToDelete(null);
        }}
      />
    </div>
  );
}

export default ArtistsOwnershipTab;
